
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { generateNotification } from '@/utils/notificationGenerator';

export const useNotificationsManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    let medicationsChannel: any;
    let appointmentsChannel: any;
    let notificationsChannel: any;
    let purchaseRequestsChannel: any;
    let messagesChannel: any;
    
    // Function to safely retrieve the current user
    const getCurrentUser = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error('Error fetching user:', error);
          return null;
        }
        
        return data?.user || null;
      } catch (err) {
        console.error('Unexpected error fetching user:', err);
        return null;
      }
    };

    const setupChannels = async () => {
      try {
        const user = await getCurrentUser();
        
        if (!user) {
          console.log('No authenticated user found, skipping realtime channel setup');
          return;
        }

        console.log('Setting up realtime channels for user:', user.id);

        // Medication notifications channel
        try {
          medicationsChannel = supabase
            .channel('medication-changes')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'medications',
                filter: `user_id=eq.${user.id}`,
              },
              (payload) => {
                console.log('Medication update:', payload);
                
                queryClient.invalidateQueries({ queryKey: ['medications'] });
                
                if (payload.eventType === 'INSERT') {
                  const notification = generateNotification({
                    type: "médicament",
                    nom: payload.new.name,
                    posologie: payload.new.dosage,
                    date: new Date().toISOString().split('T')[0],
                    heure: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  });
                  
                  toast({
                    title: notification.titre,
                    description: notification.message,
                  });
                }
              }
            )
            .subscribe((status) => {
              if (status !== 'SUBSCRIBED') {
                console.log('Medication channel subscription status:', status);
              }
            });
        } catch (err) {
          console.error('Error setting up medication channel:', err);
        }

        // Appointments notifications channel
        try {
          appointmentsChannel = supabase
            .channel('appointment-changes')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'professional_appointments',
                filter: `professional_id=eq.${user.id}`,
              },
              (payload) => {
                console.log('Appointment update:', payload);
                
                queryClient.invalidateQueries({ queryKey: ['professional-appointments'] });
                queryClient.invalidateQueries({ queryKey: ['appointments'] });
                
                if (payload.eventType === 'INSERT') {
                  toast({
                    title: "Nouvelle demande de rendez-vous",
                    description: "Vous avez reçu une nouvelle demande de rendez-vous.",
                  });
                }
              }
            )
            .subscribe((status) => {
              if (status !== 'SUBSCRIBED') {
                console.log('Appointment channel subscription status:', status);
              }
            });
        } catch (err) {
          console.error('Error setting up appointment channel:', err);
        }

        // Purchase requests notifications channel
        try {
          purchaseRequestsChannel = supabase
            .channel('purchase-request-changes')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'product_purchase_requests',
                filter: `professional_id=eq.${user.id}`,
              },
              (payload) => {
                console.log('Purchase request update:', payload);
                
                queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
                
                if (payload.eventType === 'INSERT') {
                  toast({
                    title: "Nouvelle demande d'achat",
                    description: "Vous avez reçu une nouvelle demande d'achat pour un de vos produits.",
                  });
                }
              }
            )
            .subscribe((status) => {
              if (status !== 'SUBSCRIBED') {
                console.log('Purchase requests channel subscription status:', status);
              }
            });
        } catch (err) {
          console.error('Error setting up purchase requests channel:', err);
        }

        // Messages notifications channel
        try {
          messagesChannel = supabase
            .channel('message-changes')
            .on(
              'postgres_changes',
              {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
                filter: `receiver_id=eq.${user.id}`,
              },
              (payload) => {
                console.log('New message:', payload);
                
                queryClient.invalidateQueries({ queryKey: ['messages'] });
                
                toast({
                  title: "Nouveau message",
                  description: "Vous avez reçu un nouveau message.",
                });
              }
            )
            .subscribe((status) => {
              if (status !== 'SUBSCRIBED') {
                console.log('Messages channel subscription status:', status);
              }
            });
        } catch (err) {
          console.error('Error setting up messages channel:', err);
        }

        // General notifications channel
        try {
          notificationsChannel = supabase
            .channel('db-notifications')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${user.id}`,
              },
              (payload) => {
                console.log('Notification update:', payload);
                queryClient.invalidateQueries({ queryKey: ['notifications'] });
                queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });
                
                if (payload.eventType === 'INSERT') {
                  toast({
                    title: payload.new.title,
                    description: payload.new.content,
                  });
                }
              }
            )
            .subscribe((status) => {
              if (status !== 'SUBSCRIBED') {
                console.log('Notifications channel subscription status:', status);
              }
            });
        } catch (err) {
          console.error('Error setting up notifications channel:', err);
        }
      } catch (error) {
        console.error('Error in setupChannels:', error);
      }
    };

    // Configure channels
    setupChannels().catch(err => console.error('Error in setupChannels main function:', err));

    // Cleanup
    return () => {
      console.log('Cleaning up realtime channels');
      try {
        if (medicationsChannel) supabase.removeChannel(medicationsChannel);
        if (appointmentsChannel) supabase.removeChannel(appointmentsChannel);
        if (notificationsChannel) supabase.removeChannel(notificationsChannel);
        if (purchaseRequestsChannel) supabase.removeChannel(purchaseRequestsChannel);
        if (messagesChannel) supabase.removeChannel(messagesChannel);
      } catch (err) {
        console.error('Error during channel cleanup:', err);
      }
    };
  }, [toast, queryClient]);
};
